import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Package, Plus, Music, Clock, Zap, Key, 
  MoreVertical, Download, Copy, Trash2, Edit2, 
  ChevronRight, Archive, AlertTriangle, CheckCircle2,
  Loader2, RefreshCw, Search, Filter, Sparkles, BarChart2,
  Lock, Users, Share2, Globe, Award, Upload, Image, X, Info,
  Disc3, Bell, ArrowUpDown, Calendar, Tag, Mic2
} from 'lucide-react';
import AIWizard from './AIWizard';
import StatisticsDashboard from './StatisticsDashboard';
import DJMixer from '../DJMixer';
import './MyCrates.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyCrates = ({ user, onOpenSetBuilder, onClose, onSearchInDiscovery }) => {
  const [crates, setCrates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, live, mock, archived, stats
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIWizard, setShowAIWizard] = useState(false);
  const [selectedCrate, setSelectedCrate] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [showCoverUpload, setShowCoverUpload] = useState(null); // crate object for cover upload
  const [showDJMixerModal, setShowDJMixerModal] = useState(false);
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [sortMode, setSortMode] = useState(null); // null, 'date', 'genre', 'set_type'
  const [djMixerCrate, setDjMixerCrate] = useState(null); // The crate to load in DJ Mixer

  // Release Radar state
  const [releaseRadar, setReleaseRadar] = useState([]);
  const [radarLoading, setRadarLoading] = useState(false);
  const [showRadar, setShowRadar] = useState(false);
  const [expandedRelease, setExpandedRelease] = useState(null); // mbid of expanded release
  const [releaseTracks, setReleaseTracks] = useState({}); // { mbid: [tracks] }
  const [trackLoading, setTrackLoading] = useState(null); // mbid currently loading

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('beatseek_token');
  }, []);

  // Fetch crates
  const fetchCrates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Please log in to view your crates');
        setIsLoading(false);
        return;
      }

      let url = `${BACKEND_URL}/api/crates`;
      const params = new URLSearchParams();
      
      if (filter === 'archived') {
        params.append('status', 'archived');
      } else if (filter !== 'all') {
        params.append('type', filter);
        params.append('status', 'active');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch crates');
      }

      const data = await response.json();
      setCrates(data.crates || []);
    } catch (err) {
      console.error('Error fetching crates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken, filter]);

  useEffect(() => {
    fetchCrates();
  }, [fetchCrates]);

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const menu = document.getElementById('sort-menu');
      const btn = document.querySelector('.sort-dropdown-btn');
      if (menu && !menu.contains(e.target) && !btn.contains(e.target)) {
        menu.classList.remove('show');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Create new crate
  const createCrate = async (crateData) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(crateData)
      });

      if (!response.ok) {
        throw new Error('Failed to create crate');
      }

      const newCrate = await response.json();
      setCrates(prev => [newCrate, ...prev]);
      setShowCreateModal(false);
      return newCrate;
    } catch (err) {
      console.error('Error creating crate:', err);
      throw err;
    }
  };

  // Delete crate
  const deleteCrate = async (crateId) => {
    if (!window.confirm('Are you sure you want to delete this crate?')) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete crate');
      }

      setCrates(prev => prev.filter(c => c.id !== crateId));
      setShowContextMenu(null);
    } catch (err) {
      console.error('Error deleting crate:', err);
    }
  };

  // Duplicate crate
  const duplicateCrate = async (crateId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate crate');
      }

      const data = await response.json();
      setCrates(prev => [data.crate, ...prev]);
      setShowContextMenu(null);
    } catch (err) {
      console.error('Error duplicating crate:', err);
    }
  };

  // Export crate
  const exportCrate = async (crateId, verifiedOnly = true) => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${BACKEND_URL}/api/crates/${crateId}/export?verified_only=${verifiedOnly}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to export crate');
        return;
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'crate.m3u';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowContextMenu(null);
    } catch (err) {
      console.error('Error exporting crate:', err);
    }
  };

  // Upload cover image for a crate
  const uploadCoverImage = async (crateId, imageData) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cover_image: imageData })
      });

      if (!response.ok) throw new Error('Failed to update cover');
      
      // Refresh crates to show updated cover
      fetchCrates();
      setShowCoverUpload(null);
    } catch (err) {
      console.error('Error uploading cover:', err);
      alert('Failed to upload cover image');
    }
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate days until expiration
  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Filter crates by search
  const filteredCrates = crates.filter(crate => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      crate.name?.toLowerCase().includes(query) ||
      crate.description?.toLowerCase().includes(query) ||
      crate.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  // Sort and group crates based on sortMode
  const getSortedCrates = () => {
    // All crates combined (mock, live, expired) for sorted view
    const allCrates = [...filteredCrates];
    
    if (!sortMode) {
      // Default sort by creation date (newest first)
      return allCrates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    if (sortMode === 'date') {
      return allCrates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    if (sortMode === 'genre') {
      return allCrates.sort((a, b) => {
        const genreA = (a.genre || 'zzz').toLowerCase();
        const genreB = (b.genre || 'zzz').toLowerCase();
        return genreA.localeCompare(genreB);
      });
    }
    
    if (sortMode === 'set_type') {
      return allCrates.sort((a, b) => {
        const typeA = (a.set_type || 'zzz').toLowerCase();
        const typeB = (b.set_type || 'zzz').toLowerCase();
        return typeA.localeCompare(typeB);
      });
    }
    
    return allCrates;
  };

  // Group crates by genre
  const getGroupedByGenre = () => {
    const groups = {};
    filteredCrates.forEach(crate => {
      const genre = crate.genre || 'Uncategorized';
      if (!groups[genre]) groups[genre] = [];
      groups[genre].push(crate);
    });
    // Sort groups alphabetically and sort crates within each group by date
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return sortedGroups;
  };

  // Group crates by set type
  const getGroupedBySetType = () => {
    const groups = {};
    filteredCrates.forEach(crate => {
      const setType = crate.set_type || 'other';
      const setTypeLabel = SET_TYPE_OPTIONS.find(o => o.value === setType)?.label || 'Other';
      if (!groups[setTypeLabel]) groups[setTypeLabel] = [];
      groups[setTypeLabel].push(crate);
    });
    // Sort groups alphabetically and sort crates within each group by date
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    return sortedGroups;
  };

  // Group crates by status/type (default view when no sort is active)
  const groupedCrates = {
    live: filteredCrates.filter(c => c.type === 'live' && c.status === 'active'),
    mock: filteredCrates.filter(c => c.type === 'mock' && c.status === 'active'),
    expiring: filteredCrates.filter(c => {
      const days = getDaysUntilExpiry(c.expires_at);
      return days !== null && days <= 3 && c.status === 'active';
    }),
    archived: filteredCrates.filter(c => c.status === 'archived' || c.status === 'expired')
  };

  // Get sort mode label for display
  const getSortModeLabel = () => {
    switch(sortMode) {
      case 'date': return 'Date';
      case 'genre': return 'Genre';
      case 'set_type': return 'Set Type';
      default: return 'Sort';
    }
  };

  return (
    <div className="my-crates-container" data-testid="my-crates">
      {/* Header */}
      <div className="my-crates-header">
        <div className="header-title">
          <Package size={28} className="header-icon" />
          <h2>My Crates</h2>
          <span className="crate-count">{crates.length} crates</span>
        </div>
        
        <div className="header-actions">
          <button 
            className="refresh-btn"
            onClick={fetchCrates}
            disabled={isLoading}
            data-testid="refresh-crates-btn"
          >
            <RefreshCw size={18} className={isLoading ? 'spinning' : ''} />
          </button>
          <button 
            className="ai-create-btn"
            onClick={() => setShowAIWizard(true)}
            data-testid="ai-create-btn"
          >
            <Sparkles size={18} />
            AI Curate
          </button>
          <button 
            className="create-btn"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-crate-btn"
          >
            <Plus size={18} />
            Build Your Own
          </button>
          <button 
            className="dj-mixer-btn active"
            onClick={() => {
              // Check if on mobile/tablet
              if (window.innerWidth <= 1024) {
                alert('🎧 DJ Mixer is only available on desktop.\n\nVisit BEATSEEK on a computer to access the full mixing experience!');
              } else {
                setShowDJMixerModal(true);
              }
            }}
            data-testid="dj-mixer-btn"
          >
            <Disc3 size={18} />
            DJ Mixer
          </button>
        </div>
      </div>

      {/* Release Radar */}
      <div data-testid="release-radar-section" style={{ marginBottom: '12px' }}>
        <button
          data-testid="release-radar-btn"
          onClick={async () => {
            if (showRadar) { setShowRadar(false); return; }
            if (releaseRadar.length > 0) { setShowRadar(true); return; }
            setRadarLoading(true);
            try {
              const token = getAuthToken();
              const res = await fetch(`${BACKEND_URL}/api/musicbrainz/release-radar?days=180`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
              });
              const json = await res.json();
              if (json.success) {
                setReleaseRadar(json.releases || []);
                setShowRadar(true);
              }
            } catch (e) { console.error('Release Radar error:', e); }
            setRadarLoading(false);
          }}
          style={{
            width: '100%', padding: '12px 16px',
            background: showRadar
              ? 'linear-gradient(135deg, rgba(255,0,255,0.18), rgba(180,0,255,0.12))'
              : 'linear-gradient(135deg, rgba(0,255,255,0.06), rgba(0,212,255,0.04))',
            border: `1px solid ${showRadar ? 'rgba(255,0,255,0.4)' : 'rgba(0,255,255,0.3)'}`,
            borderRadius: '12px', cursor: 'pointer',
            color: showRadar ? '#ff44ff' : '#00ffff', fontSize: '13px', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            flexDirection: 'column',
            transition: 'all 0.25s ease',
            position: 'relative', overflow: 'hidden',
          }}
        >
          {radarLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Scanning releases...
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={16} />
                {showRadar ? 'Hide Release Radar' : 'Release Radar'}
                {releaseRadar.length > 0 && !showRadar && (
                  <span style={{
                    background: '#00ffff', color: '#000', padding: '2px 10px',
                    borderRadius: '10px', fontSize: '11px', fontWeight: 800, marginLeft: '4px',
                  }}>{releaseRadar.length} new</span>
                )}
              </div>
              {!showRadar && releaseRadar.length === 0 && (
                <span style={{ fontSize: '10px', color: '#5a9a9a', fontWeight: 400, marginTop: '-2px' }}>
                  Tap to see new drops from your artists
                </span>
              )}
            </>
          )}
        </button>

        {showRadar && releaseRadar.length > 0 && (
          <div data-testid="release-radar-list" style={{
            marginTop: '8px', padding: '14px',
            background: 'linear-gradient(180deg, rgba(0,20,20,0.6), rgba(0,0,0,0.4))',
            border: '1px solid rgba(0,255,255,0.15)',
            borderRadius: '12px', maxHeight: '380px', overflowY: 'auto',
          }}>
            <div style={{ fontSize: '10px', color: '#5a9a9a', marginBottom: '12px', textAlign: 'center', letterSpacing: '0.5px' }}>
              Fresh drops from artists in your crates
            </div>
            {releaseRadar.map((r, idx) => {
              const isExpanded = expandedRelease === r.mbid;
              const typeColor = r.type === 'Album' ? '#ffa500' : r.type === 'Single' ? '#00d4ff' : '#b06aff';
              const typeBg = r.type === 'Album'
                ? 'linear-gradient(135deg, rgba(255,165,0,0.25), rgba(255,100,0,0.12))'
                : r.type === 'Single'
                ? 'linear-gradient(135deg, rgba(0,212,255,0.25), rgba(0,150,255,0.12))'
                : 'linear-gradient(135deg, rgba(138,43,226,0.25), rgba(180,80,255,0.12))';
              // Format date as relative
              const daysAgo = Math.floor((Date.now() - new Date(r.date).getTime()) / 86400000);
              const dateLabel = daysAgo <= 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : daysAgo < 30 ? `${daysAgo}d ago` : daysAgo < 365 ? `${Math.floor(daysAgo/30)}mo ago` : r.date;

              return (
                <div key={idx} data-testid={`release-radar-item-${idx}`} style={{ marginBottom: '6px' }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px',
                      background: isExpanded ? 'rgba(0,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isExpanded ? 'rgba(0,255,255,0.25)' : 'rgba(255,255,255,0.04)'}`,
                      borderRadius: isExpanded ? '10px 10px 0 0' : '10px',
                      cursor: 'pointer',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: 'translateY(0)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.background = 'rgba(0,255,255,0.08)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,255,255,0.12)';
                        e.currentTarget.style.borderColor = 'rgba(0,255,255,0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)';
                      }
                    }}
                    onClick={async () => {
                      if (isExpanded) { setExpandedRelease(null); return; }
                      setExpandedRelease(r.mbid);
                      if (!releaseTracks[r.mbid]) {
                        setTrackLoading(r.mbid);
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/musicbrainz/release-group/${r.mbid}/tracks`);
                          const data = await res.json();
                          if (data.success && data.tracks) {
                            setReleaseTracks(prev => ({ ...prev, [r.mbid]: data.tracks }));
                          }
                        } catch (e) { console.error('Track fetch error:', e); }
                        setTrackLoading(null);
                      }
                    }}
                  >
                    {/* Type Badge */}
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '10px', flexShrink: 0,
                      background: typeBg,
                      border: `1px solid ${typeColor}33`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: 800, color: typeColor,
                      letterSpacing: '0.5px',
                    }}>
                      {r.type === 'Album' ? 'LP' : r.type === 'Single' ? 'SG' : 'EP'}
                    </div>
                    {/* Release Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '1.3' }}>
                        {r.title}
                      </div>
                      <div style={{ fontSize: '11px', color: '#00ffff', fontWeight: 600, marginTop: '1px' }}>{r.artist_name}</div>
                      <div style={{ display: 'flex', gap: '5px', marginTop: '3px', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '1px 7px', fontSize: '9px', fontWeight: 700,
                          background: `${typeColor}18`, border: `1px solid ${typeColor}30`,
                          borderRadius: '6px', color: typeColor,
                        }}>{r.type}</span>
                        {r.secondary_types?.length > 0 && r.secondary_types.map((st, si) => (
                          <span key={si} style={{ padding: '1px 7px', fontSize: '9px', fontWeight: 600, background: 'rgba(255,0,255,0.1)', border: '1px solid rgba(255,0,255,0.2)', borderRadius: '6px', color: '#ff66ff' }}>{st}</span>
                        ))}
                      </div>
                    </div>
                    {/* Date + Expand Arrow */}
                    <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <div style={{ fontSize: '10px', color: daysAgo < 14 ? '#00ffff' : '#666', fontWeight: daysAgo < 14 ? 600 : 400 }}>
                        {dateLabel}
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '24px', height: '24px', borderRadius: '6px',
                        background: isExpanded ? 'rgba(0,255,255,0.2)' : 'rgba(255,255,255,0.06)',
                        border: `1px solid ${isExpanded ? 'rgba(0,255,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        transition: 'all 0.25s ease',
                      }}>
                        <ChevronRight size={14} style={{
                          color: isExpanded ? '#00ffff' : '#888',
                          transition: 'all 0.25s ease',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Track List */}
                  {isExpanded && (
                    <div style={{
                      padding: '8px 12px 10px',
                      background: 'rgba(0,255,255,0.03)',
                      border: '1px solid rgba(0,255,255,0.15)',
                      borderTop: 'none',
                      borderRadius: '0 0 10px 10px',
                    }}>
                      {trackLoading === r.mbid ? (
                        <div style={{ textAlign: 'center', padding: '8px', color: '#00ffff', fontSize: '11px' }}>
                          <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} />
                          Loading tracklist...
                        </div>
                      ) : releaseTracks[r.mbid]?.length > 0 ? (
                        <div>
                          <div style={{ fontSize: '9px', color: '#5a9a9a', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', fontWeight: 700 }}>
                            Tracklist ({releaseTracks[r.mbid].length} tracks)
                          </div>
                          {releaseTracks[r.mbid].map((track, ti) => (
                            <div key={ti} data-testid={`radar-track-${idx}-${ti}`} style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              padding: '5px 6px', marginBottom: '2px',
                              borderRadius: '6px',
                              transition: 'all 0.2s ease',
                              cursor: 'default',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(0,255,255,0.08)';
                              e.currentTarget.style.transform = 'translateX(2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                            >
                              <span style={{ fontSize: '10px', color: '#555', fontWeight: 600, width: '18px', textAlign: 'right', flexShrink: 0 }}>
                                {track.position || ti + 1}
                              </span>
                              <span style={{ fontSize: '12px', color: '#ddd', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {track.title}
                              </span>
                              {track.length_ms && (
                                <span style={{ fontSize: '10px', color: '#555', flexShrink: 0 }}>
                                  {Math.floor(track.length_ms / 60000)}:{String(Math.floor((track.length_ms % 60000) / 1000)).padStart(2, '0')}
                                </span>
                              )}
                              <button
                                data-testid={`radar-search-${idx}-${ti}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const searchQ = `${track.title} ${r.artist_name}`;
                                  if (onSearchInDiscovery) {
                                    onSearchInDiscovery(searchQ);
                                  }
                                }}
                                style={{
                                  flexShrink: 0, padding: '3px 10px',
                                  background: 'rgba(0,255,255,0.12)',
                                  border: '1px solid rgba(0,255,255,0.3)',
                                  borderRadius: '8px', cursor: 'pointer',
                                  color: '#00ffff', fontSize: '10px', fontWeight: 700,
                                  transition: 'all 0.15s ease',
                                  letterSpacing: '0.3px',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(0,255,255,0.25)';
                                  e.currentTarget.style.borderColor = 'rgba(0,255,255,0.5)';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(0,255,255,0.12)';
                                  e.currentTarget.style.borderColor = 'rgba(0,255,255,0.3)';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                title={`Find "${track.title}" in Discovery`}
                              >
                                <Search size={11} style={{ verticalAlign: 'middle' }} />
                                Find
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#555', textAlign: 'center', padding: '6px' }}>
                          No track details available
                        </div>
                      )}
                      <div style={{ fontSize: '9px', color: '#333', textAlign: 'right', marginTop: '4px' }}>
                        {r.date}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ textAlign: 'center', marginTop: '10px', padding: '4px' }}>
              <span style={{ fontSize: '9px', color: '#3a5a5a', fontStyle: 'italic' }}>Powered by MusicBrainz</span>
            </div>
          </div>
        )}

        {showRadar && releaseRadar.length === 0 && !radarLoading && (
          <div style={{ marginTop: '8px', textAlign: 'center', padding: '16px', color: '#555', fontSize: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px' }}>
            No recent releases found. Add more tracks to your crates to see new releases from those artists!
          </div>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="search-filter-bar">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search crates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            data-testid="search-crates-input"
          />
        </div>
        
        {/* Sort Dropdown */}
        <div className="sort-dropdown-wrapper">
          <button 
            className={`sort-dropdown-btn ${sortMode ? 'active' : ''}`}
            onClick={() => {
              // Toggle sort menu
              const menu = document.getElementById('sort-menu');
              if (menu) menu.classList.toggle('show');
            }}
            data-testid="sort-dropdown-btn"
          >
            <ArrowUpDown size={16} />
            {getSortModeLabel()}
          </button>
          <div id="sort-menu" className="sort-menu">
            <button 
              className={`sort-option ${sortMode === null ? 'active' : ''}`}
              onClick={() => { setSortMode(null); document.getElementById('sort-menu')?.classList.remove('show'); }}
            >
              <Package size={14} />
              Default (Grouped)
            </button>
            <button 
              className={`sort-option ${sortMode === 'date' ? 'active' : ''}`}
              onClick={() => { setSortMode('date'); document.getElementById('sort-menu')?.classList.remove('show'); }}
            >
              <Calendar size={14} />
              By Date
            </button>
            <button 
              className={`sort-option ${sortMode === 'genre' ? 'active' : ''}`}
              onClick={() => { setSortMode('genre'); document.getElementById('sort-menu')?.classList.remove('show'); }}
            >
              <Tag size={14} />
              By Genre
            </button>
            <button 
              className={`sort-option ${sortMode === 'set_type' ? 'active' : ''}`}
              onClick={() => { setSortMode('set_type'); document.getElementById('sort-menu')?.classList.remove('show'); }}
            >
              <Mic2 size={14} />
              By Set Type
            </button>
          </div>
        </div>
        
        <div className="filter-tabs">
          {[
            { id: 'all', label: 'All', icon: Package },
            { id: 'mock', label: 'Mock Crates', icon: Edit2 },
            { id: 'live', label: 'Live Sets', icon: CheckCircle2 },
            { id: 'archived', label: 'Expired', icon: Archive },
            { id: 'stats', label: 'Stats', icon: BarChart2 }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`filter-tab ${filter === id ? 'active' : ''} ${id === 'stats' ? 'stats-tab' : ''}`}
              onClick={() => setFilter(id)}
              data-testid={`filter-${id}`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="crates-content">
        {filter === 'stats' ? (
          <StatisticsDashboard onClose={() => setFilter('all')} />
        ) : isLoading ? (
          <div className="loading-state">
            <Loader2 size={40} className="spinning" />
            <p>Loading your crates...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <AlertTriangle size={40} />
            <p>{error}</p>
            <button onClick={fetchCrates}>Try Again</button>
          </div>
        ) : filteredCrates.length === 0 ? (
          <div className="empty-state">
            <Package size={60} strokeWidth={1} />
            <h3>No crates yet</h3>
            <p>Create your first crate to start building sets</p>
            <button 
              className="create-first-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} />
              Create Your First Crate
            </button>
          </div>
        ) : (
          <>
            {/* Expiring Warning - show in all views */}
            {groupedCrates.expiring.length > 0 && filter === 'all' && !sortMode && (
              <div className="expiring-warning">
                <AlertTriangle size={18} />
                <span>
                  {groupedCrates.expiring.length} crate{groupedCrates.expiring.length > 1 ? 's' : ''} expiring soon
                </span>
              </div>
            )}

            {/* SORTED VIEW - When sortMode is active, show all crates in one combined view */}
            {sortMode ? (
              <>
                {sortMode === 'date' && (
                  <div className="crate-section sorted-section">
                    <h3 className="section-title">
                      <Calendar size={18} className="sort-icon" />
                      All Crates by Date
                      <span className="section-count">{filteredCrates.length}</span>
                    </h3>
                    <div className="crates-grid">
                      {getSortedCrates().map(crate => (
                        <CrateCard 
                          key={crate.id}
                          crate={crate}
                          onOpen={() => onOpenSetBuilder?.(crate)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setShowContextMenu(crate.id);
                          }}
                          showContextMenu={showContextMenu === crate.id}
                          onCloseContextMenu={() => setShowContextMenu(null)}
                          onDelete={() => deleteCrate(crate.id)}
                          onDuplicate={() => duplicateCrate(crate.id)}
                          onExport={() => exportCrate(crate.id, crate.type === 'live')}
                          onUploadCover={(c) => setShowCoverUpload(c)}
                          onMix={() => {
                            setDjMixerCrate(crate);
                            setShowDJMixerModal(true);
                          }}
                          formatDuration={formatDuration}
                          getDaysUntilExpiry={getDaysUntilExpiry}
                          isArchived={crate.status === 'archived' || crate.status === 'expired'}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {sortMode === 'genre' && (
                  <>
                    {Object.entries(getGroupedByGenre()).map(([genre, cratez]) => (
                      <div key={genre} className="crate-section sorted-section genre-section">
                        <h3 className="section-title">
                          <Tag size={18} className="genre-icon" />
                          {genre}
                          <span className="section-count">{cratez.length}</span>
                        </h3>
                        <div className="crates-grid">
                          {cratez.map(crate => (
                            <CrateCard 
                              key={crate.id}
                              crate={crate}
                              onOpen={() => onOpenSetBuilder?.(crate)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setShowContextMenu(crate.id);
                              }}
                              showContextMenu={showContextMenu === crate.id}
                              onCloseContextMenu={() => setShowContextMenu(null)}
                              onDelete={() => deleteCrate(crate.id)}
                              onDuplicate={() => duplicateCrate(crate.id)}
                              onExport={() => exportCrate(crate.id, crate.type === 'live')}
                              onUploadCover={(c) => setShowCoverUpload(c)}
                              formatDuration={formatDuration}
                              getDaysUntilExpiry={getDaysUntilExpiry}
                              isArchived={crate.status === 'archived' || crate.status === 'expired'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {sortMode === 'set_type' && (
                  <>
                    {Object.entries(getGroupedBySetType()).map(([setType, cratez]) => (
                      <div key={setType} className="crate-section sorted-section settype-section">
                        <h3 className="section-title">
                          <Mic2 size={18} className="settype-icon" />
                          {setType}
                          <span className="section-count">{cratez.length}</span>
                        </h3>
                        <div className="crates-grid">
                          {cratez.map(crate => (
                            <CrateCard 
                              key={crate.id}
                              crate={crate}
                              onOpen={() => onOpenSetBuilder?.(crate)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setShowContextMenu(crate.id);
                              }}
                              showContextMenu={showContextMenu === crate.id}
                              onCloseContextMenu={() => setShowContextMenu(null)}
                              onDelete={() => deleteCrate(crate.id)}
                              onDuplicate={() => duplicateCrate(crate.id)}
                              onExport={() => exportCrate(crate.id, crate.type === 'live')}
                              onUploadCover={(c) => setShowCoverUpload(c)}
                              formatDuration={formatDuration}
                              getDaysUntilExpiry={getDaysUntilExpiry}
                              isArchived={crate.status === 'archived' || crate.status === 'expired'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              /* DEFAULT GROUPED VIEW - Show crates grouped by type (Live, Mock, Expired) */
              <>
                {/* Live Sets Section */}
                {(filter === 'all' || filter === 'live') && groupedCrates.live.length > 0 && (
                  <div className="crate-section">
                    <h3 className="section-title">
                      <CheckCircle2 size={18} className="live-icon" />
                      Live Sets
                      <span className="section-count">{groupedCrates.live.length}</span>
                    </h3>
                    <div className="crates-grid">
                      {groupedCrates.live.map(crate => (
                        <CrateCard 
                          key={crate.id}
                          crate={crate}
                          onOpen={() => onOpenSetBuilder?.(crate)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setShowContextMenu(crate.id);
                          }}
                          showContextMenu={showContextMenu === crate.id}
                          onCloseContextMenu={() => setShowContextMenu(null)}
                          onDelete={() => deleteCrate(crate.id)}
                          onDuplicate={() => duplicateCrate(crate.id)}
                          onExport={() => exportCrate(crate.id)}
                          onUploadCover={(c) => setShowCoverUpload(c)}
                          onMix={() => {
                            setDjMixerCrate(crate);
                            setShowDJMixerModal(true);
                          }}
                          formatDuration={formatDuration}
                          getDaysUntilExpiry={getDaysUntilExpiry}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Mock Sets Section */}
                {(filter === 'all' || filter === 'mock') && groupedCrates.mock.length > 0 && (
                  <div className="crate-section">
                    <h3 className="section-title">
                      <Edit2 size={18} className="mock-icon" />
                      Mock Sets
                      <span className="section-count">{groupedCrates.mock.length}</span>
                    </h3>
                    <div className="crates-grid">
                      {groupedCrates.mock.map(crate => (
                        <CrateCard 
                          key={crate.id}
                          crate={crate}
                          onOpen={() => onOpenSetBuilder?.(crate)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setShowContextMenu(crate.id);
                          }}
                          showContextMenu={showContextMenu === crate.id}
                          onCloseContextMenu={() => setShowContextMenu(null)}
                          onDelete={() => deleteCrate(crate.id)}
                          onDuplicate={() => duplicateCrate(crate.id)}
                          onExport={() => exportCrate(crate.id, false)}
                          onUploadCover={(c) => setShowCoverUpload(c)}
                          onMix={() => {
                            setDjMixerCrate(crate);
                            setShowDJMixerModal(true);
                          }}
                          formatDuration={formatDuration}
                          getDaysUntilExpiry={getDaysUntilExpiry}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Expired Crates Section */}
                {(filter === 'all' || filter === 'archived') && groupedCrates.archived.length > 0 && (
                  <div className="crate-section archived-section">
                    <h3 className="section-title">
                      <Archive size={18} className="archived-icon" />
                      Expired Crates
                      <span className="section-count">{groupedCrates.archived.length}</span>
                    </h3>
                    <p className="expired-notice">
                      Expired crates are kept for 2 days before permanent deletion. Extend or recover them below.
                    </p>
                    <div className="crates-grid">
                      {groupedCrates.archived.map(crate => (
                        <CrateCard 
                          key={crate.id}
                          crate={crate}
                          onOpen={() => onOpenSetBuilder?.(crate)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setShowContextMenu(crate.id);
                          }}
                          showContextMenu={showContextMenu === crate.id}
                          onCloseContextMenu={() => setShowContextMenu(null)}
                          onDelete={() => deleteCrate(crate.id)}
                          onDuplicate={() => duplicateCrate(crate.id)}
                          onExport={() => exportCrate(crate.id, false)}
                          onUploadCover={(c) => setShowCoverUpload(c)}
                          onMix={() => {
                            setDjMixerCrate(crate);
                            setShowDJMixerModal(true);
                          }}
                          formatDuration={formatDuration}
                          getDaysUntilExpiry={getDaysUntilExpiry}
                          isArchived
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Create Crate Modal */}
      {showCreateModal && (
        <CreateCrateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createCrate}
        />
      )}

      {/* AI Wizard Modal */}
      {showAIWizard && (
        <AIWizard
          onClose={() => setShowAIWizard(false)}
          onCrateCreated={(newCrate) => {
            setCrates(prev => [newCrate, ...prev]);
            setShowAIWizard(false);
          }}
        />
      )}

      {/* Cover Upload Modal */}
      {showCoverUpload && (
        <CoverUploadModal
          crate={showCoverUpload}
          onClose={() => setShowCoverUpload(null)}
          onUpload={(imageData) => uploadCoverImage(showCoverUpload.id, imageData)}
        />
      )}

      {/* DJ Mixer - Full Implementation */}
      <DJMixer 
        isOpen={showDJMixerModal}
        onClose={() => {
          setShowDJMixerModal(false);
          setDjMixerCrate(null);
        }}
        crate={djMixerCrate}
        tracks={djMixerCrate?.tracks || []}
      />
    </div>
  );
};

// Crate Card Component
const CrateCard = ({ 
  crate, 
  onOpen, 
  onContextMenu,
  showContextMenu,
  onCloseContextMenu,
  onDelete,
  onDuplicate,
  onExport,
  onUploadCover,
  onMix,
  formatDuration,
  getDaysUntilExpiry,
  isArchived = false
}) => {
  const daysLeft = getDaysUntilExpiry(crate.expires_at);
  const isExpiringSoon = daysLeft !== null && daysLeft <= 3 && !crate.is_premium_storage;
  const ownershipPct = crate.track_count > 0 
    ? Math.round((crate.verified_count / crate.track_count) * 100) 
    : 0;

  const handleCoverClick = (e) => {
    e.stopPropagation();
    if (onUploadCover) {
      onUploadCover(crate);
    }
  };

  return (
    <div 
      className={`crate-card ${crate.type} ${isArchived ? 'archived' : ''} ${isExpiringSoon ? 'expiring' : ''}`}
      onClick={onOpen}
      onContextMenu={onContextMenu}
      data-testid={`crate-card-${crate.id}`}
    >
      {/* Cover / Artwork */}
      <div className="crate-cover" onClick={handleCoverClick}>
        {crate.cover_image ? (
          <img src={crate.cover_image} alt={crate.name} />
        ) : (
          <div className="default-cover">
            <Package size={28} strokeWidth={1} />
          </div>
        )}
        <div className="cover-upload-hint">Click to add cover</div>
        
        {/* Expiring warning */}
        {isExpiringSoon && !isArchived && (
          <div className="expiring-overlay-container">
            <div className="expiring-overlay">
              <AlertTriangle size={12} />
              {daysLeft <= 0 ? 'Expires today' : `${daysLeft}d left`}
            </div>
            <button 
              className="extend-coming-soon-btn"
              onClick={(e) => {
                e.stopPropagation();
                alert('🕐 Extension Requests Coming Soon!\n\nSoon you\'ll be able to extend your crate storage:\n• 3 days extension\n• 5 days extension\n• 10 days extension\n\nStay tuned!');
              }}
              title="Extension requests coming soon"
            >
              <Clock size={10} />
              Extend
            </button>
          </div>
        )}
        
        {/* Expired crate recovery overlay */}
        {isArchived && (
          <div className="expired-crate-overlay">
            <div className="expired-badge">
              <AlertTriangle size={12} />
              Expired
            </div>
            <button 
              className="recover-coming-soon-btn"
              onClick={(e) => {
                e.stopPropagation();
                alert('🔄 Crate Recovery Coming Soon!\n\nSoon you\'ll be able to recover expired crates:\n• 3 days recovery\n• 5 days recovery\n• 10 days recovery\n\nExpired crates are kept for 2 days before permanent deletion.\n\nStay tuned!');
              }}
              title="Recovery coming soon"
            >
              <RefreshCw size={10} />
              Recover
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="crate-info">
        <div className="crate-header">
          <h4 className="crate-name">{crate.name}</h4>
          <div 
            className={`collab-badge ${crate.collab_mode || 'private'}`}
            title={
              crate.collab_mode === 'collab_set' ? 'Full Collaboration' :
              crate.collab_mode === 'public' ? 'Public' :
              crate.collab_mode === 'private_suggestions' ? 'Private + Suggestions' :
              'Private'
            }
          >
            {crate.collab_mode === 'collab_set' ? (
              <Users size={10} />
            ) : crate.collab_mode === 'public' ? (
              <Globe size={10} />
            ) : crate.collab_mode === 'private_suggestions' ? (
              <Users size={10} />
            ) : (
              <Lock size={10} />
            )}
          </div>
        </div>
        
        {/* Quick Stats Row */}
        <div className="crate-quick-stats">
          <span className="qs-item">
            <Music size={12} />
            {crate.track_count || 0}
          </span>
          <span className="qs-divider">•</span>
          <span className="qs-item">
            <Clock size={12} />
            {formatDuration(crate.total_duration_ms)}
          </span>
          {crate.bpm_range && (
            <>
              <span className="qs-divider">•</span>
              <span className="qs-item bpm">
                {crate.bpm_range.min}-{crate.bpm_range.max}
              </span>
            </>
          )}
        </div>

        {/* Ownership Bar */}
        {crate.track_count > 0 && (
          <div className="ownership-bar">
            <div className="bar-track">
              <div 
                className="bar-fill"
                style={{ width: `${ownershipPct}%` }}
              />
            </div>
            <span className="bar-pct">{ownershipPct}%</span>
          </div>
        )}

        {/* Tags */}
        {crate.tags && crate.tags.length > 0 && (
          <div className="crate-tags">
            {crate.tags.slice(0, 3).map(tag => (
              <span key={tag} className="tag">#{tag}</span>
            ))}
          </div>
        )}

        {/* Footer: Type, Energy, Battle Tested */}
        <div className="crate-footer">
          <span className={`crate-type-text ${crate.type}`}>
            {crate.type === 'live' ? 'Live Set' : 'Mock Set'}
          </span>
          {crate.avg_energy && (
            <span className="crate-energy-text">
              <Zap size={11} /> {crate.avg_energy}/10
            </span>
          )}
          {crate.battle_tested && (
            <span className="crate-battle-text">
              Battle Tested
            </span>
          )}
        </div>
      </div>

      {/* Context Menu Button */}
      <button 
        className="context-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          onContextMenu(e);
        }}
        data-testid={`crate-menu-${crate.id}`}
      >
        <MoreVertical size={18} />
      </button>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="context-menu-overlay"
            onClick={(e) => {
              e.stopPropagation();
              onCloseContextMenu();
            }}
          />
          <div className="context-menu" onClick={(e) => e.stopPropagation()}>
            <button onClick={onOpen}>
              <ChevronRight size={16} />
              Open
            </button>
            {onMix && crate.track_count > 0 && (
              <button onClick={onMix} className="mix-btn">
                <Disc3 size={16} />
                DJ Mix
              </button>
            )}
            <button onClick={onExport}>
              <Download size={16} />
              Export M3U
            </button>
            <button onClick={onDuplicate}>
              <Copy size={16} />
              Duplicate
            </button>
            <div className="menu-divider" />
            <button className="delete-btn" onClick={onDelete}>
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Genre options for dropdown
const GENRE_OPTIONS = [
  'House', 'Tech House', 'Deep House', 'Progressive House',
  'Techno', 'Melodic Techno', 'Hard Techno', 'Minimal Techno',
  'Trance', 'Progressive Trance', 'Uplifting Trance', 'Psytrance',
  'Drum & Bass', 'Jungle', 'Breakbeat', 'UK Garage',
  'Dubstep', 'Bass Music', 'Future Bass', 'Trap',
  'Disco', 'Nu-Disco', 'Funk', 'Soul',
  'Afro House', 'Amapiano', 'Latin House', 'Tribal',
  'Electro', 'EBM', 'Industrial', 'Synthwave',
  'Ambient', 'Downtempo', 'Chillout', 'Lo-Fi',
  'Hip Hop', 'R&B', 'Reggaeton', 'Dancehall',
  'Multi-Genre', 'Open Format', 'Other'
];

// Set type options (what kind of gig/context)
const SET_TYPE_OPTIONS = [
  { value: 'club', label: 'Club Night' },
  { value: 'festival', label: 'Festival' },
  { value: 'livestream', label: 'Livestream' },
  { value: 'radio', label: 'Radio Show' },
  { value: 'mobile', label: 'Mobile / Event' },
  { value: 'wedding', label: 'Wedding' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'house_party', label: 'House Party' },
  { value: 'bedroom', label: 'Bedroom / Practice' },
  { value: 'warmup', label: 'Warm-up Set' },
  { value: 'peak_time', label: 'Peak Time' },
  { value: 'closing', label: 'Closing Set' },
  { value: 'afterhours', label: 'After Hours' },
  { value: 'other', label: 'Other' }
];

// Create Crate Modal Component
const CreateCrateModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [crateType, setCrateType] = useState('mock');
  const [collabMode, setCollabMode] = useState('private');
  const [setType, setSetType] = useState('');
  const [genre, setGenre] = useState('');
  const [tags, setTags] = useState('');
  const [coverOption, setCoverOption] = useState('none');
  const [coverPreview, setCoverPreview] = useState(null);
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const coverInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const coverOptions = [
    { id: 'mosaic', label: 'Mosaic', icon: Package, disabled: true },
    { id: 'upload', label: 'Upload', icon: Upload, disabled: false },
    { id: 'first_track', label: '1st Track', icon: Music, disabled: true },
    { id: 'ai_generated', label: 'AI Generated', icon: Sparkles, disabled: false },
    { id: 'none', label: 'None', icon: X, disabled: false }
  ];

  const handleCoverOptionSelect = async (optionId) => {
    if (optionId === 'upload') {
      coverInputRef.current?.click();
      return;
    }

    if (optionId === 'ai_generated') {
      if (!genre) {
        setError('Select a genre first to generate AI cover');
        return;
      }
      setIsGeneratingCover(true);
      setError(null);
      try {
        const token = localStorage.getItem('beatseek_token');
        // Use a temporary endpoint that generates based on genre/tags without crate
        const response = await fetch(`${BACKEND_URL}/api/crates/generate-cover-preview`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ genre, set_type: setType, tags: tags.split(',').filter(Boolean) })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to generate cover');
        }
        
        const data = await response.json();
        if (data.cover_image) {
          setCoverPreview(data.cover_image);
          setCoverOption('ai_generated');
        }
      } catch (err) {
        setError(err.message || 'AI generation failed');
      }
      setIsGeneratingCover(false);
      return;
    }

    if (optionId === 'none') {
      setCoverPreview(null);
      setCoverOption('none');
      return;
    }
  };

  const handleCoverFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Cover image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPreview(e.target.result);
        setCoverOption('upload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter a crate name');
      return;
    }

    setIsCreating(true);
    setError(null);

    // Build tags array including genre and set type as tags
    const allTags = tags.split(',').map(t => t.trim()).filter(Boolean);
    if (genre && !allTags.includes(genre.toLowerCase())) {
      allTags.unshift(genre.toLowerCase().replace(/\s+/g, '-'));
    }
    if (setType) {
      const setTypeLabel = SET_TYPE_OPTIONS.find(s => s.value === setType)?.label;
      if (setTypeLabel && !allTags.includes(setType)) {
        allTags.push(setType);
      }
    }

    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || null,
        type: crateType,
        collab_mode: collabMode,
        is_public: collabMode === 'public' || collabMode === 'collab_set',
        set_type: setType || null,
        genre: genre || null,
        tags: allTags,
        cover_image: coverPreview
      });
    } catch (err) {
      setError(err.message);
      setIsCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-crate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create New Crate</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="crate-name">Crate Name *</label>
            <input
              id="crate-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Saturday Night Tech House"
              autoFocus
              data-testid="crate-name-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="crate-description">Description</label>
            <textarea
              id="crate-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this set about?"
              rows={2}
              data-testid="crate-description-input"
            />
          </div>

          <div className="form-group cover-upload-group">
            <label>Cover Art (optional) <span className="cover-hint">add tracks for more options</span></label>
            <div className="cover-options-inline">
              {coverOptions.map(({ id, label, icon: Icon, disabled }) => (
                <button
                  key={id}
                  type="button"
                  className={`cover-opt-btn ${coverOption === id ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => !disabled && handleCoverOptionSelect(id)}
                  disabled={disabled || isGeneratingCover}
                  title={disabled ? 'Add tracks first' : label}
                >
                  {isGeneratingCover && id === 'ai_generated' ? (
                    <Loader2 size={12} className="spinning" />
                  ) : (
                    <Icon size={12} />
                  )}
                  <span>{label}</span>
                </button>
              ))}
              {coverPreview && (
                <div className="cover-preview-mini">
                  <img src={coverPreview} alt="Cover" />
                </div>
              )}
            </div>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          <div className="form-group">
            <label>Crate Type</label>
            <div className="type-selector">
              <button
                type="button"
                className={`type-option ${crateType === 'mock' ? 'active' : ''}`}
                onClick={() => setCrateType('mock')}
                data-testid="type-mock-btn"
              >
                <div 
                  className="type-info-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info size={14} />
                  <div className="type-info-tooltip">
                    <p>Mock Set: Perfect for planning future sets. Add any track regardless of ownership. Great for wish lists, set ideas, and discovering new music to buy.</p>
                    <p className="tooltip-warning"><AlertTriangle size={12} /> Not available for download.</p>
                  </div>
                </div>
                <Edit2 size={20} />
                <span className="type-label">Mock</span>
                <span className="type-desc">For planning - any tracks</span>
              </button>
              <button
                type="button"
                className={`type-option ${crateType === 'live' ? 'active' : ''}`}
                onClick={() => setCrateType('live')}
                data-testid="type-live-btn"
              >
                <div 
                  className="type-info-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info size={14} />
                  <div className="type-info-tooltip">
                    <p>Live Set: For gig-ready sets. Only allows tracks you own or have verified access to. Export directly to DJ software.</p>
                  </div>
                </div>
                <CheckCircle2 size={20} />
                <span className="type-label">Live</span>
                <span className="type-desc">Only verified tracks</span>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Privacy & Collaboration</label>
            <div className="collab-selector">
              <button
                type="button"
                className={`collab-option collab-private ${collabMode === 'private' ? 'active' : ''}`}
                onClick={() => setCollabMode('private')}
                data-testid="collab-private-btn"
              >
                <Lock size={18} />
                <span className="collab-label">Private</span>
                <span className="collab-desc">Only you can view & edit</span>
              </button>
              <button
                type="button"
                className={`collab-option collab-suggestions ${collabMode === 'private_suggestions' ? 'active' : ''}`}
                onClick={() => setCollabMode('private_suggestions')}
                data-testid="collab-suggestions-btn"
              >
                <Users size={18} />
                <span className="collab-label">+ Suggestions</span>
                <span className="collab-desc">Invite users to suggest tracks</span>
              </button>
              <button
                type="button"
                className={`collab-option collab-public ${collabMode === 'public' ? 'active' : ''}`}
                onClick={() => setCollabMode('public')}
                data-testid="collab-public-btn"
              >
                <Globe size={18} />
                <span className="collab-label">Public</span>
                <span className="collab-desc">Anyone can view & suggest</span>
              </button>
              <button
                type="button"
                className={`collab-option collab-full ${collabMode === 'collab_set' ? 'active' : ''}`}
                onClick={() => setCollabMode('collab_set')}
                data-testid="collab-full-btn"
              >
                <Users size={18} />
                <span className="collab-label">Full Collab</span>
                <span className="collab-desc">Collaborators can edit directly</span>
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="set-type">Set Type</label>
              <select
                id="set-type"
                value={setType}
                onChange={(e) => setSetType(e.target.value)}
                data-testid="set-type-select"
              >
                <option value="">Select set type...</option>
                {SET_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="genre">Genre</label>
              <select
                id="genre"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                data-testid="genre-select"
              >
                <option value="">Select genre...</option>
                {GENRE_OPTIONS.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="crate-tags">Additional Tags (comma separated)</label>
            <input
              id="crate-tags"
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="tech-house, peak-time, club"
              data-testid="crate-tags-input"
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="create-btn"
              disabled={isCreating || !name.trim()}
              data-testid="submit-create-crate"
            >
              {isCreating ? (
                <>
                  <Loader2 size={18} className="spinning" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={18} />
                  Create Crate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Cover Upload Modal Component
const CoverUploadModal = ({ crate, onClose, onUpload }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [preview, setPreview] = useState(crate?.cover_image || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const coverOptions = [
    { id: 'mosaic', label: 'Track Mosaic', desc: 'Collage of album art', icon: Package },
    { id: 'upload', label: 'Upload Image', desc: 'Your own artwork', icon: Upload },
    { id: 'first_track', label: 'First Track', desc: 'Use first track art', icon: Music },
    { id: 'ai_generated', label: 'AI Generated', desc: 'Based on genre/vibe', icon: Sparkles },
    { id: 'none', label: 'No Image', desc: 'Keep it minimal', icon: X }
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
        setSelectedOption('upload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOptionSelect = async (optionId) => {
    setSelectedOption(optionId);
    setError(null);

    if (optionId === 'upload') {
      fileInputRef.current?.click();
      return;
    }

    if (optionId === 'none') {
      setPreview(null);
      return;
    }

    if (optionId === 'first_track') {
      // Use first track's album art if available
      const firstTrack = crate?.tracks?.[0];
      if (firstTrack?.album_art || firstTrack?.artwork_url || firstTrack?.image) {
        setPreview(firstTrack.album_art || firstTrack.artwork_url || firstTrack.image);
      } else {
        setError('No album art available for first track');
        setSelectedOption(null);
      }
      return;
    }

    if (optionId === 'mosaic') {
      // Generate mosaic from track artwork client-side
      setIsProcessing(true);
      try {
        const trackImages = crate?.tracks
          ?.filter(t => t.album_art || t.artwork_url || t.image)
          ?.slice(0, 4)
          ?.map(t => t.album_art || t.artwork_url || t.image);
        
        if (!trackImages || trackImages.length < 2) {
          setError('Need at least 2 tracks with artwork for mosaic');
          setSelectedOption(null);
          setIsProcessing(false);
          return;
        }
        
        // Generate mosaic using canvas
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        
        // Fill background
        ctx.fillStyle = '#0a0e27';
        ctx.fillRect(0, 0, 400, 200);
        
        // Load and draw images
        const loadImage = (src) => {
          return new Promise((resolve) => {
            const img = new window.Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = src;
          });
        };
        
        const images = await Promise.all(trackImages.map(loadImage));
        const validImages = images.filter(img => img !== null);
        
        if (validImages.length >= 2) {
          const cols = validImages.length >= 4 ? 2 : validImages.length;
          const rows = validImages.length >= 3 ? 2 : 1;
          const cellWidth = 400 / cols;
          const cellHeight = 200 / rows;
          
          validImages.slice(0, 4).forEach((img, i) => {
            const x = (i % cols) * cellWidth;
            const y = Math.floor(i / cols) * cellHeight;
            ctx.drawImage(img, x, y, cellWidth, cellHeight);
          });
          
          setPreview(canvas.toDataURL('image/jpeg', 0.85));
        } else {
          setError('Could not load track artwork');
          setSelectedOption(null);
        }
      } catch (err) {
        setError('Failed to generate mosaic');
        setSelectedOption(null);
      }
      setIsProcessing(false);
      return;
    }

    if (optionId === 'ai_generated') {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem('beatseek_token');
        const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/generate-cover`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to generate cover');
        }
        
        const data = await response.json();
        if (data.cover_image) {
          setPreview(data.cover_image);
        } else {
          throw new Error('No image returned');
        }
      } catch (err) {
        setError(err.message || 'AI generation failed');
        setSelectedOption(null);
      }
      setIsProcessing(false);
      return;
    }
  };

  const handleSave = async () => {
    setIsProcessing(true);
    await onUpload(preview);
    setIsProcessing(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="cover-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Crate Cover Art</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="cover-upload-content">
          {/* Preview */}
          <div className="cover-large-preview">
            {isProcessing ? (
              <div className="processing-state">
                <Loader2 size={32} className="spinning" />
                <span>Generating...</span>
              </div>
            ) : preview ? (
              <img src={preview} alt="Cover preview" />
            ) : (
              <div className="no-cover">
                <Image size={36} />
                <span>No cover image</span>
              </div>
            )}
          </div>

          {error && <div className="cover-error">{error}</div>}

          {/* Options Grid */}
          <div className="cover-options-grid">
            {coverOptions.map(({ id, label, desc, icon: Icon }) => (
              <button
                key={id}
                className={`cover-option ${selectedOption === id ? 'active' : ''} ${id === 'none' ? 'none-option' : ''}`}
                onClick={() => handleOptionSelect(id)}
                disabled={isProcessing}
              >
                <Icon size={18} />
                <span className="option-label">{label}</span>
                <span className="option-desc">{desc}</span>
              </button>
            ))}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button 
            className="save-btn"
            onClick={handleSave}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><Loader2 size={16} className="spinning" /> Saving...</>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyCrates;
